import type { Trace } from '@/features/traceability/type/Tool';
import type { FilterRule, FilterMode, FilterDimension } from '../type/dynamicChart';
import { filterDimensionRegistry } from './filterDimensions';

/**
 * Apply a single filter rule to a raw value extracted from a trace.
 * Returns true if the trace passes the rule (before negate is applied).
 * Returns false if rawValue is null (null values never match).
 */
function applyRule(rule: FilterRule, rawValue: string | number | null): boolean {
  if (rawValue === null) return false;

  const { operator, values } = rule;

  switch (operator) {
    case 'in':
      return values.includes(String(rawValue));

    case 'range': {
      const min = parseFloat(values[0]);
      const max = parseFloat(values[1]);
      if (isNaN(min) || isNaN(max) || min > max) return true; // invalid range: silent pass
      return Number(rawValue) >= min && Number(rawValue) <= max;
    }

    case 'gte': {
      const threshold = parseFloat(values[0]);
      if (isNaN(threshold)) return true;
      return Number(rawValue) >= threshold;
    }

    case 'lte': {
      const threshold = parseFloat(values[0]);
      if (isNaN(threshold)) return true;
      return Number(rawValue) <= threshold;
    }

    case 'date_range': {
      const strVal = String(rawValue);
      return strVal >= values[0] && strVal <= values[1];
    }

    case 'after':
      return String(rawValue) >= values[0];

    case 'before':
      return String(rawValue) <= values[0];

    default:
      return true;
  }
}

/**
 * Apply a list of filter rules to traces.
 *
 * @param traces - All traces to filter
 * @param rules - Array of FilterRule objects
 * @param filterMode - 'ALL' = all rules must pass (AND); 'ANY' = any rule must pass (OR)
 * @returns Filtered array of traces
 */
export function applyFilters(
  traces: Trace[],
  rules: FilterRule[],
  filterMode: FilterMode
): Trace[] {
  if (rules.length === 0) return traces;

  return traces.filter((trace) => {
    let matchCount = 0;

    for (const rule of rules) {
      const dimDef = filterDimensionRegistry.find((d) => d.key === rule.dimension);
      if (!dimDef) {
        // Unknown dimension — defensive pass
        matchCount++;
        continue;
      }

      const rawValue = dimDef.extract(trace);
      let ruleResult = applyRule(rule, rawValue);

      if (rule.negate) {
        ruleResult = !ruleResult;
      }

      if (ruleResult) {
        matchCount++;
      }
    }

    if (filterMode === 'ALL') {
      return matchCount === rules.length;
    } else {
      return matchCount > 0;
    }
  });
}

/**
 * Migrate a legacy ChartFilters object (pre-F3) to a FilterRule[].
 * If the input is already an array, it is returned unchanged (shallow check).
 */
export function migrateChartFilters(legacy: unknown): FilterRule[] {
  if (Array.isArray(legacy)) return legacy as FilterRule[];
  if (typeof legacy !== 'object' || !legacy) return [];

  const old = legacy as Record<string, unknown>;
  const rules: FilterRule[] = [];

  const addRule = (dim: FilterDimension, values: unknown) => {
    if (Array.isArray(values) && values.length > 0) {
      rules.push({
        id: crypto.randomUUID(),
        dimension: dim,
        operator: 'in',
        values: values as string[],
        negate: false,
      });
    }
  };

  addRule('machine', old['machines']);
  addRule('material', old['materials']);
  addRule('toolHolder', old['toolHolders']);
  addRule('lubrication', old['lubrications']);

  return rules;
}
